/**
 * agentVisuals.ts — Shared visual config for agent domain→icon/color/category mapping.
 * Used by AgentHub, CreateAgentPage, EcosystemPage, etc.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Package,
  Image,
  BookOpen,
  Cpu,
  Radio,
  ArrowDownToLine,
  ArrowUpFromLine,
  GitBranch,
  Users,
  LayoutDashboard,
  Wrench,
  Shield,
  Settings,
  ClipboardCheck,
  Bot,
  Brain,
  Plug,
  Workflow,
  Zap,
  Leaf,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Categories                                                         */
/* ------------------------------------------------------------------ */

export interface CategoryDef {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Tailwind color token — e.g. 'indigo', 'blue', 'emerald' */
  color: string;
  order: number;
}

export const CATEGORIES: CategoryDef[] = [
  { id: 'data-pipeline',       label: 'Data Pipeline',        icon: GitBranch,       color: 'indigo',  order: 1 },
  { id: 'standards-catalog',   label: 'Standards & Catalog',  icon: BookOpen,        color: 'blue',    order: 2 },
  { id: 'pim',                 label: 'PIM & Assets',         icon: Package,         color: 'sky',     order: 3 },
  { id: 'agriculture',         label: 'Agriculture',          icon: Leaf,            color: 'green',   order: 4 },
  { id: 'integration-iot',     label: 'Integration & IoT',    icon: Cpu,             color: 'emerald', order: 5 },
  { id: 'intelligence',        label: 'Intelligence',         icon: Brain,           color: 'cyan',    order: 6 },
  { id: 'system',              label: 'System & Admin',       icon: Shield,          color: 'slate',   order: 7 },
  { id: 'uncategorized',       label: 'Other',                icon: Bot,             color: 'gray',    order: 99 },
];

export const CATEGORY_MAP = new Map(CATEGORIES.map((c) => [c.id, c]));

/* ------------------------------------------------------------------ */
/*  Per-domain visuals                                                 */
/* ------------------------------------------------------------------ */

export interface DomainVisual {
  icon: LucideIcon;
  /** bg-{color}-100 */
  bgLight: string;
  /** text-{color}-600 */
  textColor: string;
  /** Top-stripe gradient: from-{color}-500 to-{color}-400 */
  stripe: string;
  /** Category id — must match one of CATEGORIES[].id */
  categoryId: string;
}

const DOMAIN_VISUALS: Record<string, DomainVisual> = {
  // ── Data Pipeline ──────────────────────────────────────────────
  dataflow: {
    icon: Workflow,
    bgLight: 'bg-indigo-100',
    textColor: 'text-indigo-600',
    stripe: 'from-indigo-500 to-indigo-400',
    categoryId: 'data-pipeline',
  },
  apis: {
    icon: ArrowDownToLine,
    bgLight: 'bg-amber-100',
    textColor: 'text-amber-600',
    stripe: 'from-amber-500 to-amber-400',
    categoryId: 'data-pipeline',
  },
  api_ingest: {
    icon: ArrowDownToLine,
    bgLight: 'bg-amber-100',
    textColor: 'text-amber-600',
    stripe: 'from-amber-500 to-amber-400',
    categoryId: 'data-pipeline',
  },
  api_expose: {
    icon: ArrowUpFromLine,
    bgLight: 'bg-violet-100',
    textColor: 'text-violet-600',
    stripe: 'from-violet-500 to-violet-400',
    categoryId: 'data-pipeline',
  },
  api_management: {
    icon: GitBranch,
    bgLight: 'bg-indigo-100',
    textColor: 'text-indigo-600',
    stripe: 'from-indigo-500 to-indigo-400',
    categoryId: 'data-pipeline',
  },
  app: {
    icon: Workflow,
    bgLight: 'bg-indigo-100',
    textColor: 'text-indigo-600',
    stripe: 'from-indigo-500 to-indigo-400',
    categoryId: 'data-pipeline',
  },

  // ── Standards & Catalog ────────────────────────────────────────
  contextualizer: {
    icon: BookOpen,
    bgLight: 'bg-orange-100',
    textColor: 'text-orange-600',
    stripe: 'from-orange-500 to-orange-400',
    categoryId: 'standards-catalog',
  },
  taxonomy: {
    icon: BookOpen,
    bgLight: 'bg-orange-100',
    textColor: 'text-orange-600',
    stripe: 'from-orange-500 to-orange-400',
    categoryId: 'standards-catalog',
  },

  // ── PIM & Assets ───────────────────────────────────────────────
  pim: {
    icon: Package,
    bgLight: 'bg-sky-100',
    textColor: 'text-sky-600',
    stripe: 'from-sky-500 to-sky-400',
    categoryId: 'pim',
  },
  dam: {
    icon: Image,
    bgLight: 'bg-pink-100',
    textColor: 'text-pink-600',
    stripe: 'from-pink-500 to-pink-400',
    categoryId: 'pim',
  },
  relationships: {
    icon: Users,
    bgLight: 'bg-cyan-100',
    textColor: 'text-cyan-600',
    stripe: 'from-cyan-500 to-cyan-400',
    categoryId: 'pim',
  },

  // ── Agriculture ────────────────────────────────────────────────
  agriculture: {
    icon: Leaf,
    bgLight: 'bg-green-100',
    textColor: 'text-green-600',
    stripe: 'from-green-500 to-green-400',
    categoryId: 'agriculture',
  },
  agronomy: {
    icon: Leaf,
    bgLight: 'bg-green-100',
    textColor: 'text-green-600',
    stripe: 'from-green-500 to-green-400',
    categoryId: 'agriculture',
  },
  irrigation: {
    icon: Leaf,
    bgLight: 'bg-teal-100',
    textColor: 'text-teal-600',
    stripe: 'from-teal-500 to-teal-400',
    categoryId: 'agriculture',
  },
  soil: {
    icon: Leaf,
    bgLight: 'bg-amber-100',
    textColor: 'text-amber-600',
    stripe: 'from-amber-500 to-amber-400',
    categoryId: 'agriculture',
  },
  remote_sensing: {
    icon: Cpu,
    bgLight: 'bg-green-100',
    textColor: 'text-green-600',
    stripe: 'from-green-500 to-green-400',
    categoryId: 'agriculture',
  },
  market: {
    icon: ArrowUpFromLine,
    bgLight: 'bg-green-100',
    textColor: 'text-green-600',
    stripe: 'from-green-500 to-green-400',
    categoryId: 'agriculture',
  },
  weather: {
    icon: Radio,
    bgLight: 'bg-sky-100',
    textColor: 'text-sky-600',
    stripe: 'from-sky-500 to-sky-400',
    categoryId: 'agriculture',
  },
  equipment: {
    icon: Wrench,
    bgLight: 'bg-orange-100',
    textColor: 'text-orange-600',
    stripe: 'from-orange-500 to-orange-400',
    categoryId: 'agriculture',
  },
  alerts: {
    icon: Radio,
    bgLight: 'bg-red-100',
    textColor: 'text-red-600',
    stripe: 'from-red-500 to-red-400',
    categoryId: 'agriculture',
  },
  reporting: {
    icon: ClipboardCheck,
    bgLight: 'bg-green-100',
    textColor: 'text-green-600',
    stripe: 'from-green-500 to-green-400',
    categoryId: 'agriculture',
  },
  documents: {
    icon: BookOpen,
    bgLight: 'bg-green-100',
    textColor: 'text-green-600',
    stripe: 'from-green-500 to-green-400',
    categoryId: 'agriculture',
  },
  agri_drone_ops: {
    icon: Zap,
    bgLight: 'bg-green-100',
    textColor: 'text-green-600',
    stripe: 'from-green-500 to-green-400',
    categoryId: 'agriculture',
  },
  // ── App-scoped defaults ───────────────────────────────────────
  health: {
    icon: Brain,
    bgLight: 'bg-violet-100',
    textColor: 'text-violet-600',
    stripe: 'from-violet-500 to-violet-400',
    categoryId: 'intelligence',
  },
  memory: {
    icon: Brain,
    bgLight: 'bg-cyan-100',
    textColor: 'text-cyan-600',
    stripe: 'from-cyan-500 to-cyan-400',
    categoryId: 'intelligence',
  },
  interpreter: {
    icon: ArrowDownToLine,
    bgLight: 'bg-amber-100',
    textColor: 'text-amber-600',
    stripe: 'from-amber-500 to-amber-400',
    categoryId: 'data-pipeline',
  },

  // ── Integration & IoT ─────────────────────────────────────────
  iot: {
    icon: Radio,
    bgLight: 'bg-emerald-100',
    textColor: 'text-emerald-600',
    stripe: 'from-emerald-500 to-emerald-400',
    categoryId: 'integration-iot',
  },
  mcp: {
    icon: Plug,
    bgLight: 'bg-teal-100',
    textColor: 'text-teal-600',
    stripe: 'from-teal-500 to-teal-400',
    categoryId: 'integration-iot',
  },
  telemetry: {
    icon: Radio,
    bgLight: 'bg-teal-100',
    textColor: 'text-teal-600',
    stripe: 'from-teal-500 to-teal-400',
    categoryId: 'integration-iot',
  },

  // ── Intelligence ───────────────────────────────────────────────
  coordinator: {
    icon: Brain,
    bgLight: 'bg-violet-100',
    textColor: 'text-violet-600',
    stripe: 'from-violet-500 to-violet-400',
    categoryId: 'intelligence',
  },

  // ── Catalog (Asset Management) ──────────────────────────────────
  asset_management: {
    icon: Package,
    bgLight: 'bg-sky-100',
    textColor: 'text-sky-600',
    stripe: 'from-sky-500 to-sky-400',
    categoryId: 'pim',
  },

  // ── Entity Management (Identity) ──────────────────────────────
  entity_management: {
    icon: Users,
    bgLight: 'bg-slate-100',
    textColor: 'text-slate-600',
    stripe: 'from-slate-500 to-slate-400',
    categoryId: 'system',
  },
  identity: {
    icon: Users,
    bgLight: 'bg-slate-100',
    textColor: 'text-slate-600',
    stripe: 'from-slate-500 to-slate-400',
    categoryId: 'system',
  },
  standards_catalog: {
    icon: BookOpen,
    bgLight: 'bg-blue-100',
    textColor: 'text-blue-600',
    stripe: 'from-blue-500 to-blue-400',
    categoryId: 'standards-catalog',
  },

  // ── System & Admin ─────────────────────────────────────────────
  system: {
    icon: Shield,
    bgLight: 'bg-slate-100',
    textColor: 'text-slate-600',
    stripe: 'from-slate-500 to-slate-400',
    categoryId: 'system',
  },
  meta: {
    icon: Shield,
    bgLight: 'bg-slate-100',
    textColor: 'text-slate-600',
    stripe: 'from-slate-500 to-slate-400',
    categoryId: 'system',
  },
  audit: {
    icon: ClipboardCheck,
    bgLight: 'bg-slate-100',
    textColor: 'text-slate-600',
    stripe: 'from-slate-500 to-slate-400',
    categoryId: 'system',
  },
  settings: {
    icon: Settings,
    bgLight: 'bg-gray-100',
    textColor: 'text-gray-600',
    stripe: 'from-gray-500 to-gray-400',
    categoryId: 'system',
  },
  'app-manager': {
    icon: LayoutDashboard,
    bgLight: 'bg-purple-100',
    textColor: 'text-purple-600',
    stripe: 'from-purple-500 to-purple-400',
    categoryId: 'system',
  },
};

/** Fallback for unknown domains */
const DEFAULT_VISUAL: DomainVisual = {
  icon: Bot,
  bgLight: 'bg-gray-100',
  textColor: 'text-gray-500',
  stripe: 'from-gray-400 to-gray-300',
  categoryId: 'uncategorized',
};

/** Get visual config for a domain string */
export function getDomainVisual(domain?: string): DomainVisual {
  if (!domain) return DEFAULT_VISUAL;
  return DOMAIN_VISUALS[domain] ?? DEFAULT_VISUAL;
}

/** Resolve a specific icon for agents that span sub-domains (e.g. api-ingest vs api-expose) */
export function getAgentIcon(agent: { id?: string; agent_id?: string; domain?: string }): LucideIcon {
  const id = (agent.agent_id || agent.id || '').toLowerCase();
  // Special overrides for agents within the same domain
  if (id.includes('api-expose') || id.includes('apiexpose')) return ArrowUpFromLine;
  if (id.includes('api-ingest') || id.includes('apiingest')) return ArrowDownToLine;
  if (id.includes('trekker')) return Zap;
  return getDomainVisual(agent.domain).icon;
}

/** Get the category for a domain */
export function getCategoryForDomain(domain?: string): CategoryDef {
  const vis = getDomainVisual(domain);
  return CATEGORY_MAP.get(vis.categoryId) ?? CATEGORIES[CATEGORIES.length - 1];
}
